import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, Search as SearchIcon } from '@mui/icons-material';
import { getProducts } from '../../services/ProductService';
import { SALE_ORIGIN, resolveChannelPrice } from '../../utils/Enums';
import { DEFAULT_CURRENCY } from '../../utils/currency';
import { money } from './tablesUtils';

const productCurrency = (product) => product.priceCurrency || DEFAULT_CURRENCY;

/**
 * Arma un pedido (una tanda) para la mesa. Solo muestra productos con precio de venta local; si la cuenta ya tiene
 * ítems, solo los de su misma moneda (un ticket = una moneda).
 */
const ProductPickerDialog = ({ open, locationId, lockedCurrency, onClose, onSubmit, submitting }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [order, setOrder] = useState({}); // productId -> { product, quantity, notes }

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setOrder({});
    setSearch('');
    setLoading(true);
    getProducts(locationId, 1, 100000, '')
      .then((data) => {
        if (!active) return;
        const list = Array.isArray(data) ? data : data?.products || data?.data || [];
        setProducts(list);
      })
      .catch(() => active && setProducts([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [open, locationId]);

  const sellable = useMemo(
    () =>
      products
        .filter((product) => resolveChannelPrice(product, SALE_ORIGIN.LOCAL) != null)
        .filter((product) => !lockedCurrency || productCurrency(product) === lockedCurrency)
        .sort((a, b) => (a.alias || a.name).localeCompare(b.alias || b.name)),
    [products, lockedCurrency]
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sellable;
    return sellable.filter((product) => `${product.name} ${product.alias || ''}`.toLowerCase().includes(term));
  }, [sellable, search]);

  const lines = Object.values(order);
  const orderCurrency = lines[0] ? productCurrency(lines[0].product) : lockedCurrency;
  const total = lines.reduce((sum, line) => sum + line.quantity * resolveChannelPrice(line.product, SALE_ORIGIN.LOCAL), 0);

  const add = (product) => {
    if (orderCurrency && productCurrency(product) !== orderCurrency) return;
    setOrder((previous) => {
      const current = previous[product.id];
      return { ...previous, [product.id]: { product, quantity: (current?.quantity || 0) + 1, notes: current?.notes || '' } };
    });
  };

  const change = (productId, delta) =>
    setOrder((previous) => {
      const current = previous[productId];
      if (!current) return previous;
      const quantity = current.quantity + delta;
      if (quantity <= 0) {
        const { [productId]: _removed, ...rest } = previous;
        return rest;
      }
      return { ...previous, [productId]: { ...current, quantity } };
    });

  const setNotes = (productId, notes) =>
    setOrder((previous) => ({ ...previous, [productId]: { ...previous[productId], notes } }));

  const submit = () =>
    onSubmit(
      lines.map((line) => ({ productId: line.product.id, quantity: line.quantity, notes: line.notes.trim() || null }))
    );

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Agregar productos</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: 1.4, minWidth: 0 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar producto"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ mb: 1.5 }}
            />
            {lockedCurrency && lockedCurrency !== DEFAULT_CURRENCY && (
              <Chip size="small" label={`Solo productos en ${lockedCurrency}`} sx={{ mb: 1 }} />
            )}
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: 1,
                  maxHeight: '48vh',
                  overflow: 'auto',
                  pr: 0.5,
                }}
              >
                {visible.map((product) => {
                  const blocked = orderCurrency && productCurrency(product) !== orderCurrency;
                  return (
                    <Paper
                      key={product.id}
                      variant="outlined"
                      onClick={() => !blocked && add(product)}
                      sx={{
                        p: 1,
                        cursor: blocked ? 'not-allowed' : 'pointer',
                        opacity: blocked ? 0.45 : 1,
                        '&:hover': { borderColor: blocked ? undefined : 'primary.main' },
                      }}
                    >
                      <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                        {product.alias || product.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {money(resolveChannelPrice(product, SALE_ORIGIN.LOCAL), productCurrency(product))}
                      </Typography>
                    </Paper>
                  );
                })}
                {visible.length === 0 && (
                  <Typography color="text.secondary" variant="body2">
                    No hay productos para mostrar.
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" gutterBottom>Pedido</Typography>
            {lines.length === 0 ? (
              <Typography color="text.secondary" variant="body2">Tocá un producto para agregarlo.</Typography>
            ) : (
              <Stack spacing={1} sx={{ maxHeight: '48vh', overflow: 'auto' }}>
                {lines.map((line) => (
                  <Paper key={line.product.id} variant="outlined" sx={{ p: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>{line.product.alias || line.product.name}</Typography>
                      <IconButton size="small" onClick={() => change(line.product.id, -1)}><RemoveIcon fontSize="small" /></IconButton>
                      <Typography variant="body2" sx={{ minWidth: 20, textAlign: 'center' }}>{line.quantity}</Typography>
                      <IconButton size="small" onClick={() => change(line.product.id, 1)}><AddIcon fontSize="small" /></IconButton>
                    </Stack>
                    <TextField
                      fullWidth
                      size="small"
                      variant="standard"
                      placeholder="Nota (ej. sin cebolla)"
                      value={line.notes}
                      inputProps={{ maxLength: 255 }}
                      onChange={(event) => setNotes(line.product.id, event.target.value)}
                    />
                  </Paper>
                ))}
              </Stack>
            )}
            {lines.length > 0 && (
              <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1.5 }}>
                Total del pedido: {money(total, orderCurrency)}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
        <Button variant="contained" disabled={lines.length === 0 || submitting} onClick={submit}>
          {submitting ? <CircularProgress size={20} /> : 'Enviar a la mesa'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductPickerDialog;
